import React, { useState, useEffect } from "react";
import { Card } from "primereact/card";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Skeleton } from "primereact/skeleton";
import { useTenantInformationLayout } from "./TenantInformationLayout";
import useTenants from "../useTenants";
import { Link, useParams, useLocation } from "react-router-dom";
import TenantAuditSidebar from "~/components/audit-sidebar/TenantAuditSidebar";
import AdminOnly from "~/components/AdminOnly";
import { useQueryClient } from "@tanstack/react-query";
import { useTenantUserAssignmentsAudit } from "~/api/tenants/hooks/useTenantUserAssignmentsAudit";
import CustomeButton from "~/components/Button";

const AssignedUsers = () => {
  const { id } = useParams();
  const location = useLocation();
  const {
    tenant,
    onAssignUser,
    onUnAssignUser,
    onViewHistory,
    setRefetchUsersCallback,
  } = useTenantInformationLayout();
  const { fetchTenantUsers } = useTenants();
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortField, setSortField] = useState("first_name");
  const [sortOrder, setSortOrder] = useState(1); // 1 = asc, -1 = desc
  const skeletonRows = [...Array(10)];
  const [isAuditSidebarVisible, setAuditSidebarVisible] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    fetchUsers(1, rows, sortField, sortOrder === 1 ? "asc" : "desc");
  }, [id]);

  // GXM-1783: Check for navigation state to bypass cache after user edit
  useEffect(() => {
    if (location.state?.refetchUsers) {
      fetchUsers(
        Math.floor(first / rows) + 1,
        rows,
        sortField,
        sortOrder === 1 ? "asc" : "desc",
        true, // skipCache
      );
      // Clear the state to prevent refetching on subsequent renders
      window.history.replaceState({}, document.title);
    }
  }, [location.state?.refetchUsers]);

  // Register the fetchUsers callback with the context
  // Pass skipCache=true to bypass APIM cache after mutations
  useEffect(() => {
    if (setRefetchUsersCallback) {
      setRefetchUsersCallback(
        () => () =>
          fetchUsers(
            Math.floor(first / rows) + 1,
            rows,
            sortField,
            sortOrder === 1 ? "asc" : "desc",
            true,
          ),
      );
    }
  }, [setRefetchUsersCallback, first, rows, sortField, sortOrder]);

  const fetchUsers = async (
    page = 1,
    pageSize = 10,
    sortBy = "first_name",
    order = "asc",
    skipCache = false,
  ) => {
    setUsersLoading(true);
    try {
      // GXM-1800: Use sparse fieldset to optimize response size
      // Request only fields needed for the assigned users view
      // Note: access field removed as it's not displayed in this view
      const fields = "id,first_name,last_name,email,role";

      const response = await fetchTenantUsers(
        id,
        page,
        pageSize,
        sortBy,
        order,
        skipCache,
        fields,
      );
      // Extract users from nested response structure
      const usersList = response?.data?.users || response?.users || [];
      setTotalRecords(response?.data?.pagination?.total || 0);
      setUsers(usersList);
    } catch (error) {
      console.error("Error fetching users:", error);
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  };

  const onPageChange = (e) => {
    const newPage = Math.floor(e.first / e.rows) + 1;
    setFirst(e.first);
    setRows(e.rows);
    fetchUsers(newPage, e.rows, sortField, sortOrder === 1 ? "asc" : "desc");
  };

  const onSort = (e) => {
    setSortField(e.sortField);
    setSortOrder(e.sortOrder);
    const newPage = Math.floor(first / rows) + 1;
    // Send empty sortBy/sortOrder to API when sorting by company
    if (e.sortField === "company") {
      fetchUsers(newPage, rows, "", "");
    } else {
      fetchUsers(
        newPage,
        rows,
        e.sortField,
        e.sortOrder === 1 ? "asc" : "desc",
      );
    }
  };

  const getFullName = (rowData) => {
    const firstName = rowData.first_name || "";
    const lastName = rowData.last_name || "";
    return `${firstName} ${lastName}`.trim();
  };

  const auditLabels = () => ({
    create: "User Assigned",
    update: "User Assignment Updated",
    delete: "User Unassigned",
  });

  return (
    <Card className="gen-card core-tenant-info__main-content">
      <div className="core-tenant-info__container">
        <div className="core-tenant-info__section">
          <div className="core-tenant-info__section-header">
            <div className="core-tenant-info__section-header-text">
              <h3 className="core-tenant-info__section-title">
                Assigned Users
              </h3>
              <p className="core-tenant-info__section-subtitle">
                View all users currently assigned to this tenant.
              </p>
            </div>
            <div className="core-tenant-info__actions">
              <AdminOnly>
                <CustomeButton
                  label="View History"
                  variant="outlined"
                  onClick={() => setAuditSidebarVisible(true)}
                  icon="pi pi-history"
                  iconPos="right"
                  className="mr-2"
                />
              </AdminOnly>
              <button className="btn btn-primary" onClick={onAssignUser}>
                <i className="pi pi-plus mr-2"></i>
                Assign GXM User
              </button>
            </div>
          </div>

          <DataTable
            value={usersLoading ? skeletonRows : users}
            className="core-tenant-info__users-table datatable-right-align"
            stripedRows
            responsiveLayout="scroll"
            scrollable
            scrollHeight="flex"
            lazy
            paginator={!usersLoading && totalRecords > 10}
            first={first}
            rows={rows}
            onPage={onPageChange}
            totalRecords={totalRecords}
            sortField={sortField}
            sortOrder={sortOrder}
            onSort={onSort}
            rowsPerPageOptions={[10, 20, 30]}
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
            emptyMessage="No users assigned"
          >
            <Column
              field="first_name"
              header="Full Name"
              sortable
              body={(data) => {
                if (usersLoading) {
                  return <Skeleton width="100%" />;
                }
                const fullName = getFullName(data);
                if (!fullName) {
                  return "-";
                }
                return (
                  <Link
                    to={`/tenants/${id}/users/${data.id}`}
                    className="user-link"
                  >
                    {fullName}
                  </Link>
                );
              }}
              style={{ width: "30%" }}
            />
            <Column
              field="email"
              header="Contact Email"
              sortable
              body={(data) =>
                usersLoading ? <Skeleton width="100%" /> : data.email
              }
              style={{ width: "35%" }}
            />
            <Column
              field="company"
              header="Company"
              sortable
              body={(data) =>
                usersLoading ? <Skeleton width="100%" /> : data.company
              }
              style={{ width: "25%" }}
            />
            <Column
              body={(rowData) =>
                usersLoading ? (
                  <Skeleton width="100%" />
                ) : (
                  <button
                    className="btn-icon-delete"
                    title="Delete user"
                    onClick={() => onUnAssignUser(rowData)}
                  >
                    <i className="pi pi-trash"></i>
                  </button>
                )
              }
              style={{ width: "10%", textAlign: "center" }}
            />
          </DataTable>
        </div>
      </div>
      {isAuditSidebarVisible && (
        <TenantAuditSidebar
          heading="Assigned Users"
          tenantId={id}
          setSidebarToggle={setAuditSidebarVisible}
          sidebarToggle={isAuditSidebarVisible}
          labels={auditLabels}
          useAuditHook={useTenantUserAssignmentsAudit}
        />
      )}
    </Card>
  );
};

export default AssignedUsers;
